import { FormDataFree } from "../reactHookFreeScale";
import { FormDataLopre } from "../reactHookLopreScale";
import { RiskLevels, returnValue } from "./shared";

export type ScaleFreeIndex = {
    score2: number;
    countryRisk: string;
    ldlCholesterol: number;
    egfr: number;
    riskLevel: string;
    eventProbability: string;
    cholesterolTarget: { target: number; cholesterolType: string };
    smoke: boolean;
    bmi: number | null;
};

/**
 * Soglie oltre le quali un parametro conta come fattore di rischio.
 *
 * Vengono dal documento del cliente "algoritmo scala open con correzioni
 * 25/08/2026" e dai suoi commenti a margine: ipertensione e dislipidemia non
 * hanno le stesse soglie in tutte le sezioni dell'algoritmo, quindi ogni
 * livello di rischio usa il proprio set.
 *
 * Confermate dal cliente e valide in tutte le sezioni: ipertensione sopra
 * 140/90 e trigliceridi sopra 150 mg/dL, anche dove i commenti al documento
 * riportavano 130/80 e 130. Al momento fra un livello e l'altro cambia quindi
 * solo la soglia di LDL, ma i tre set restano distinti perché il documento
 * definisce i fattori sezione per sezione.
 */
export type RiskFactorThresholds = {
    /** Ipertensione se la pressione sistolica supera questo valore. */
    systolicPressure: number;
    /** Ipertensione se la pressione diastolica supera questo valore. */
    diastolicPressure: number;
    /** Dislipidemia se i trigliceridi superano questo valore. */
    triglycerides: number;
    /** Dislipidemia se il colesterolo LDL supera questo valore. */
    ldlCholesterol: number;
};

export const riskFactorThresholds: Record<"veryHigh" | "high" | "moderate", RiskFactorThresholds> =
    {
        veryHigh: {
            systolicPressure: 140,
            diastolicPressure: 90,
            triglycerides: 150,
            ldlCholesterol: 70,
        },
        high: {
            systolicPressure: 140,
            diastolicPressure: 90,
            triglycerides: 150,
            ldlCholesterol: 70,
        },
        moderate: {
            systolicPressure: 140,
            diastolicPressure: 90,
            triglycerides: 150,
            ldlCholesterol: 100,
        },
    };

/** Ipertensione: in terapia antipertensiva oppure pressione sopra soglia. */
export function hasHypertension(formData: FormDataFree, thresholds: RiskFactorThresholds): boolean {
    return (
        !!formData.hypertensionMedications ||
        formData.systolicPressure > thresholds.systolicPressure ||
        formData.diastolicPressure > thresholds.diastolicPressure
    );
}

/** Dislipidemia: in terapia ipolipemizzante oppure profilo lipidico alterato. */
export function hasDyslipidemia(
    formData: FormDataFree,
    ldlCholesterol: number,
    thresholds: RiskFactorThresholds
): boolean {
    const hdlLimit = formData.sex === "male" ? 40 : 50;
    return (
        !!formData.cholesterolMedications ||
        formData.triglycerides > thresholds.triglycerides ||
        formData.hdlCholesterol < hdlLimit ||
        ldlCholesterol > thresholds.ldlCholesterol
    );
}

/**
 * Conta i fattori di rischio del paziente.
 *
 * Il terzo fattore cambia a seconda del criterio: per l'ipercolesterolemia
 * familiare il documento elenca "ipertensione, fumo, diabete mellito", per i
 * criteri sul diabete "ipertensione, fumo, dislipidemia".
 */
export function riskFactors(
    formData: FormDataFree,
    ldlCholesterol: number,
    smoke: boolean,
    thresholds: RiskFactorThresholds,
    thirdFactor: "diabetes" | "dyslipidemia"
): number {
    let riskCount: number = 0;
    riskCount += smoke ? 1 : 0;
    riskCount += hasHypertension(formData, thresholds) ? 1 : 0;
    if (thirdFactor === "diabetes") {
        riskCount += formData.diabetesSwitch ? 1 : 0;
    } else {
        riskCount += hasDyslipidemia(formData, ldlCholesterol, thresholds) ? 1 : 0;
    }
    return riskCount;
}

/**
 * Calcola il livello di rischio della scala open e il relativo target di LDL.
 *
 * Implementa l'algoritmo del documento del cliente "algoritmo scala open con
 * correzioni 25/08/2026". Rispetto alla versione precedente: le soglie SCORE2
 * non dipendono più dall'età (>=20 molto alto, 10-20 alto, 2-10 moderato,
 * <2 basso) e i fattori di rischio sono fumo, ipertensione e dislipidemia
 * (oppure diabete, nei criteri sull'ipercolesterolemia familiare).
 *
 * @param {FormDataFree} formData - I dati del modulo.
 * @param {number} score2value - Il punteggio Score2 calcolato.
 * @param {number} ldlCholesterol - Il livello di lipoproteine a bassa densità (LDL).
 * @param {number} egfr - Il tasso di filtrazione glomerulare stimato (eGFR).
 * @param {boolean} smoke - Indica se il paziente è considerato fumatore.
 * @returns {[keyof typeof RiskLevels, number]} Livello di rischio e target di LDL in mg/dL.
 */
export function getRiskLevelValue(
    formData: FormDataFree,
    score2value: number,
    ldlCholesterol: number,
    egfr: number,
    smoke: boolean
): [keyof typeof RiskLevels, number] {
    const factors = (
        level: keyof typeof riskFactorThresholds,
        thirdFactor: "diabetes" | "dyslipidemia"
    ) => riskFactors(formData, ldlCholesterol, smoke, riskFactorThresholds[level], thirdFactor);

    //placche ateromasiche/aterosclerosi con stenosi > 50% o sintomatiche
    const plaqueWithStenosis = formData.arteriopathy && formData.arteryStenosis;
    //anni trascorsi dalla diagnosi di diabete (diabetesAge è l'età alla diagnosi)
    const diabetesYears =
        formData.diabetesSwitch && formData.diabetesAge
            ? formData.age - formData.diabetesAge
            : null;

    //RISCHIO ESTREMO
    if (
        //più di un evento cardiovascolare o equivalente
        (formData.cardiovascularEventSwitch && formData.moreCardiovascularEvent) ||
        //evento cardiovascolare o equivalente + placche con stenosi > 50% o sintomatiche
        (formData.cardiovascularEventSwitch && plaqueWithStenosis) ||
        //placche con stenosi > 50% o sintomatiche in più di un distretto arterioso
        formData.multiDistrictStenosis
    ) {
        return returnValue("extreme");
    }

    //RISCHIO MOLTO ALTO
    if (
        //SCORE2 >= 20%
        score2value >= 20 ||
        //placche ateromasiche/aterosclerosi con stenosi > 50% o sintomatiche
        plaqueWithStenosis ||
        //un evento cardiovascolare o equivalente
        formData.cardiovascularEventSwitch ||
        //FH con almeno un altro fattore di rischio (ipertensione, fumo, diabete)
        (formData.familyHychol && factors("veryHigh", "diabetes") >= 1) ||
        //MRC severa (eGFR < 30)
        egfr < 30 ||
        //DM1 o DM2 con danno d'organo o più di 2 fattori (ipertensione, fumo, dislipidemia)
        (formData.diabetesSwitch &&
            (formData.diabetesDiseases || factors("veryHigh", "dyslipidemia") > 2)) ||
        //DM1 da almeno 20 anni
        (formData.diabetesSelect === "diabete1" && diabetesYears !== null && diabetesYears >= 20)
    ) {
        return returnValue("veryHigh");
    }

    //RISCHIO ALTO
    if (
        //SCORE2 >= 10% e < 20%
        (score2value >= 10 && score2value < 20) ||
        //placche ateromasiche/aterosclerosi con stenosi < 50% o asintomatiche
        (formData.arteriopathy && !formData.arteryStenosis) ||
        //colesterolo o pressione molto elevati
        formData.totalCholesterol > 310 ||
        ldlCholesterol > 190 ||
        formData.systolicPressure >= 180 ||
        formData.diastolicPressure >= 110 ||
        //FH e nessun altro fattore di rischio
        (formData.familyHychol && factors("high", "diabetes") < 1) ||
        //MRC moderata (eGFR 30-59)
        (egfr >= 30 && egfr < 60) ||
        //DM2 senza danno d'organo, con durata di almeno 10 anni oppure almeno un altro fattore
        (formData.diabetesSwitch &&
            formData.diabetesSelect === "diabete2" &&
            !formData.diabetesDiseases &&
            ((diabetesYears !== null && diabetesYears >= 10) ||
                factors("high", "dyslipidemia") >= 1))
    ) {
        return returnValue("high");
    }

    //RISCHIO BASSO: SCORE2 < 2% (i diabetici restano almeno moderati)
    if (score2value < 2 && !formData.diabetesSwitch) {
        return returnValue("low");
    }

    //Giovane diabetico da meno di 10 anni, senza danno d'organo e senza altri
    //fattori di rischio. La condizione è ridondante finché la banda moderata è
    //"SCORE2 < 10%", ma la teniamo esplicita per tracciabilità col documento.
    const youngDiabetes =
        formData.diabetesSwitch &&
        !formData.diabetesDiseases &&
        diabetesYears !== null &&
        diabetesYears < 10 &&
        factors("moderate", "dyslipidemia") === 0 &&
        ((formData.diabetesSelect === "diabete1" && formData.age < 35) ||
            (formData.diabetesSelect === "diabete2" && formData.age < 50));

    //RISCHIO MODERATO: SCORE2 tra 2% e 10% (sopra il 10% è già stato gestito)
    if (score2value < 10 || youngDiabetes) {
        return returnValue("moderateLow");
    }
    //TODO: come gestire l'errore?
    return ["moderateLow", -1];
}

/**
 * Calcola il punteggio Martin-Hopkins basato su determinati parametri di input.
 *
 * @param {FormDataFree} formData - I dati del modulo.
 * @returns {number[]} Un array di valori numerici che rappresentano il punteggio calcolato.
 *   - [0] - Punteggio trigliceridi (a): 0-28 a seconda dei trigliceridi.
 *   - [1] - Punteggio colesterolo non HDL (b): 0-5 a seconda del colesterolo non HDL.
 * @throws {Error} Se i parametri di input sono invalidi.
 */
export function getMartinHopkinsValue(formData: FormDataFree | FormDataLopre): number[] {
    const { triglycerides, hdlCholesterol, totalCholesterol } = formData;
    const noHdlCholesterol: number = totalCholesterol - hdlCholesterol;
    let a, b;
    if (triglycerides >= 7 && triglycerides <= 49) {
        a = 0;
    } else if (triglycerides > 49 && triglycerides <= 56) {
        a = 1;
    } else if (triglycerides > 56 && triglycerides <= 61) {
        a = 2;
    } else if (triglycerides > 61 && triglycerides <= 66) {
        a = 3;
    } else if (triglycerides > 66 && triglycerides <= 71) {
        a = 4;
    } else if (triglycerides > 71 && triglycerides <= 75) {
        a = 5;
    } else if (triglycerides > 75 && triglycerides <= 79) {
        a = 6;
    } else if (triglycerides > 79 && triglycerides <= 83) {
        a = 7;
    } else if (triglycerides > 83 && triglycerides <= 87) {
        a = 8;
    } else if (triglycerides > 87 && triglycerides <= 92) {
        a = 9;
    } else if (triglycerides > 92 && triglycerides <= 96) {
        a = 10;
    } else if (triglycerides > 96 && triglycerides <= 100) {
        a = 11;
    } else if (triglycerides > 100 && triglycerides <= 105) {
        a = 12;
    } else if (triglycerides > 105 && triglycerides <= 110) {
        a = 13;
    } else if (triglycerides > 110 && triglycerides <= 115) {
        a = 14;
    } else if (triglycerides > 115 && triglycerides <= 120) {
        a = 15;
    } else if (triglycerides > 120 && triglycerides <= 126) {
        a = 16;
    } else if (triglycerides > 126 && triglycerides <= 132) {
        a = 17;
    } else if (triglycerides > 132 && triglycerides <= 138) {
        a = 18;
    } else if (triglycerides > 138 && triglycerides <= 146) {
        a = 19;
    } else if (triglycerides > 146 && triglycerides <= 154) {
        a = 20;
    } else if (triglycerides > 154 && triglycerides <= 163) {
        a = 21;
    } else if (triglycerides > 163 && triglycerides <= 173) {
        a = 22;
    } else if (triglycerides > 173 && triglycerides <= 185) {
        a = 23;
    } else if (triglycerides > 185 && triglycerides <= 201) {
        a = 24;
    } else if (triglycerides > 201 && triglycerides <= 220) {
        a = 25;
    } else if (triglycerides > 220 && triglycerides <= 247) {
        a = 26;
    } else if (triglycerides > 247 && triglycerides <= 292) {
        a = 27;
    } else if (triglycerides > 292 && triglycerides <= 399) {
        a = 27;
    } else if (triglycerides > 399 && triglycerides <= 13975) {
        a = 28;
    } else {
        throw new Error("invalid triglycerides");
    }

    if (noHdlCholesterol <= 100) {
        b = 0;
    } else if (noHdlCholesterol > 100 && noHdlCholesterol <= 129) {
        b = 1;
    } else if (noHdlCholesterol > 129 && noHdlCholesterol <= 159) {
        b = 2;
    } else if (noHdlCholesterol > 159 && noHdlCholesterol <= 189) {
        b = 3;
    } else if (noHdlCholesterol > 189 && noHdlCholesterol <= 219) {
        b = 4;
    } else {
        b = 5;
    }

    return [a, b];
}

